import { FC, useMemo } from "react";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import { useNavigate } from "react-router-dom";
import rehypeSanitize from "rehype-sanitize";
import remarkBreaks from "remark-breaks";

import {
  ENTITY_SCHEME,
  injectEntityLinks,
  TAiUiElement,
  useEntityHref,
} from "@ryde/shared";

import { EditLinkStyled } from "../grid/generic/cells/cell-link.styled";
import { MARKDOWN_SCHEMA } from "./markdown-message.constants";
import { EntityChipStyled, RuleStyled } from "./markdown-message.styled";

/**
 * Keep our own entity scheme; sanitize every other URL the normal way.
 *
 * react-markdown blanks any href whose protocol is not http/https/irc/mailto/xmpp, BEFORE
 * the `a` override or the rehype sanitizer ever see it. `ryde-entity:` fell into that net,
 * so every chip arrived with `href=""`, missed the entity branch below, and rendered as a
 * link that navigated nowhere — the "clicking the id does nothing" report. Allowing the
 * scheme in the sanitizer schema was necessary but not sufficient; both gates blank it.
 *
 * This is not a hole in the URL policy: `ryde-entity:` never reaches the browser. The
 * override resolves it to an in-app route and drops it when the entity has no screen.
 */
export const entityAwareUrlTransform = (url: string): string =>
  url.startsWith(ENTITY_SCHEME) ? url : defaultUrlTransform(url);

interface IMarkdownMessageProps {
  text: string;
  allowOnlyInnerLinks?: boolean;
  /** Assistant elements for this turn; only `entityRef` entries are used here. */
  elements?: TAiUiElement[];
}

export const MarkdownMessage: FC<IMarkdownMessageProps> = ({
  text,
  allowOnlyInnerLinks = false,
  elements,
}) => {
  const navigate = useNavigate();
  const entityHref = useEntityHref();

  // Strips model-authored links and rewrites registered entity mentions into our private
  // scheme, handled by the `a` override below.
  const prepared = useMemo(
    () => injectEntityLinks(text, elements),
    [text, elements],
  );

  const isInnerLink = (href: string): boolean => {
    return (
      href.startsWith("/") || href.startsWith("#/") || !href.includes("://")
    );
  };

  return (
    <ReactMarkdown
      rehypePlugins={[[rehypeSanitize, MARKDOWN_SCHEMA]]}
      remarkPlugins={[remarkBreaks]}
      urlTransform={entityAwareUrlTransform}
      components={{
        hr: () => <RuleStyled />,
        a: ({ href = "", children }) => {
          if (href.startsWith(ENTITY_SCHEME)) {
            const [entity, rawId] = href.slice(ENTITY_SCHEME.length).split("/");
            const chipHref = entityHref(entity, Number(rawId) || undefined);
            if (!chipHref) {
              // Unroutable entity: plain text beats a chip that goes nowhere.
              return <>{children}</>;
            }
            return (
              <EntityChipStyled
                href={chipHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {children}
              </EntityChipStyled>
            );
          }

          if (!href) {
            // A link whose href was sanitized away is a control that swallows the click.
            // Text is the honest rendering of it.
            return <>{children}</>;
          }

          if (allowOnlyInnerLinks && !isInnerLink(href)) {
            // External link → render as text
            return <>{children}</>;
          }

          return (
            <EditLinkStyled
              onClick={() => {
                navigate(href);
              }}
            >
              {children}
            </EditLinkStyled>
          );
        },
      }}
    >
      {prepared}
    </ReactMarkdown>
  );
};
